import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader, OrbitControls } from 'three-stdlib';
import { VRMLoaderPlugin } from '@pixiv/three-vrm';
import { Box, Button, Stack, Typography } from '@pankod/refine-mui';
import { useNotification } from '@pankod/refine-core';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import { CONST } from 'config/const';

export const PreviewVRM: React.FC<{ url: string }> = ({ url }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { open } = useNotification();
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 1.5, 2);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current!,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);

    // Camera position

    // Lighting
    const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(1, 1, 1).normalize();
    scene.add(light);

    // VRM Loader
    const loader = new GLTFLoader();
    loader.register(
      (parser) => new VRMLoaderPlugin(parser as any) as any
    );

    // OrbitControls for Camera Interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // Smooth damping effect
    controls.dampingFactor = 0.1;
    controls.target.set(0, 1.5, 0); // Look at the center of the VRM model
    controls.update();

    loader.load(
      url,
      (gltf) => {
        const vrm = gltf.userData.vrm; // VRM model is stored in glTF userData
        if (vrm) {
          scene.add(vrm.scene);

          // Animation loop
          const clock = new THREE.Clock();
          const animate = () => {
            const deltaTime = clock.getDelta();
            vrm.update(deltaTime);
            controls.update();
            renderer.render(scene, camera);
            requestAnimationFrame(animate);
          };
          animate();
        }
      },
      (progress) => {
        setIsLoading(progress.loaded < progress.total);
      },
      (error) => {
        console.log("🚀 [Debug] ~ useEffect ~ error:", error)
        open?.({
          type: 'error',
          message: error?.message,
        });
      }
    );

    // Cleanup on component unmount
    return () => {
      renderer.dispose();
      controls.dispose();
      // revoke the object URL
    };
  }, [url]);

  return (
    <Box
      sx={{
        position: 'relative',
        marginLeft: '-24px',
        marginTop: '-24px',
        width: 'calc(100% + 48px)',
        height: 'calc(100% + 48px)',
        zIndex: 1000,
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          if (isLoading) {
            return;
          }
          //remove preview query param
          searchParams.delete(CONST.PARAMS.PREVIEW_VRM);
          setSearchParams(searchParams);
        }}
        disabled={isLoading}
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          left: '10px',
          zIndex: 10,
        }}
      >
        {isLoading ? 'Loading...' : 'Back'}
      </Button>
      <canvas ref={canvasRef}></canvas>
    </Box>
  );
};
